var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import * as Rx from 'rxjs';
import { distinctUntilChanged, filter, map } from 'rxjs/operators';
import { arrayIsShallowEqual, isDefined, toObservable, } from './helpers';
var NullQuery = /** @class */ (function () {
    function NullQuery() {
    }
    Object.defineProperty(NullQuery.prototype, "query", {
        get: function () {
            return {
                limit$: undefined,
                params$: undefined,
                order$: undefined,
            };
        },
        enumerable: true,
        configurable: true
    });
    return NullQuery;
}());
export { NullQuery };
export function isInequalityParam(param) {
    var op = param.condition;
    return op === '<=' || op === '>=' || op === '<' || op === '>';
}
/**
 * Used to filter, limit and sort objects in a collection. Refine your view of
 * the collection by adding clauses with their respective methods.
 *
 * Every method accepts an Observable in place of the value. If any clause
 * uses an Observable value, that Observable MUST emit something before the
 * collection can be fetched. By extension if you use multiple clauses, their
 * values must all emit before the collection can emit.
 */
var Query = /** @class */ (function () {
    function Query(params$, limitValue$, orderByValue$) {
        this.params$ = params$;
        this.limitValue$ = limitValue$;
        this.orderByValue$ = orderByValue$;
    }
    Query.newQuery = function () {
        return new Query(undefined, undefined, undefined);
    };
    Query.prototype.where = function (field, condition, value$) {
        var _this = this;
        var newParam$ = toObservable(value$).pipe(condition === 'in'
            ? distinctUntilChanged(arrayIsShallowEqual)
            : distinctUntilChanged(), map(function (v) {
            // massive ternary is because otherwise type error
            var param = condition === 'in'
                ? { name: field, condition: condition, values: v }
                : condition === '=='
                    ? { name: field, condition: condition, value: v }
                    : condition === '<='
                        ? { name: field, condition: condition, value: v }
                        : condition === '<'
                            ? { name: field, condition: condition, value: v }
                            : condition === '>='
                                ? { name: field, condition: condition, value: v }
                                : condition === '>'
                                    ? { name: field, condition: condition, value: v }
                                    : condition === 'includes'
                                        ? { name: field, condition: condition, value: v }
                                        : condition === 'intersects'
                                            ? { name: field, condition: condition, values: v }
                                            : condition === 'equalsIgnoreOrder'
                                                ? { name: field, condition: condition, values: v }
                                                : condition === 'nearby'
                                                    ? { name: field, condition: condition, value: v }
                                                    : { name: field, condition: condition, value: v };
            return param;
        }));
        // First where clause added
        if (this.params$ === undefined) {
            return new Query(newParam$.pipe(map(function (param) {
                var _a;
                return (_a = {},
                    _a[field] = [param],
                    _a);
            })), this.limitValue$, this.orderByValue$);
        }
        return new Query(Rx.combineLatest([this.params$, newParam$]).pipe(filter(function (_a) {
            var currParams = _a[0], newParam = _a[1];
            return _this.isValidWhereClause(currParams, newParam, field);
        }), map(function (_a) {
            var currParams = _a[0], newParam = _a[1];
            return _this.mergeParams(currParams, newParam);
        }), distinctUntilChanged()), this.limitValue$, this.orderByValue$);
    };
    Query.prototype.mergeParams = function (currParams, newParam) {
        var _a, _b;
        if (currParams[newParam.name]) {
            // Concat param under existing key
            return __assign({}, currParams, (_a = {}, _a[newParam.name] = currParams[newParam.name].concat([newParam]), _a));
        }
        return __assign({}, currParams, (_b = {}, _b[newParam.name] = [newParam], _b));
    };
    Query.prototype.isValidWhereClause = function (currParams, newParam, field) {
        var existingClauses = currParams[field];
        // A new clause on a field is always allowed
        if (!isDefined(existingClauses)) {
            return true;
        }
        // There must be exactly one non-inequality clause
        if (!isInequalityParam(newParam) ||
            existingClauses.some(function (param) { return !isInequalityParam(param); })) {
            throw new Error("Duplicate where clause on field '" + field + "' is not allowed");
        }
        if (!this.isValidInequalities(existingClauses.concat([newParam]))) {
            throw new Error("Multiple inequality filters on the field '" + field + "' must form a valid range");
        }
        return true;
    };
    Query.prototype.isValidInequalities = function (clauses) {
        return (!!clauses.length &&
            clauses.length === 2 &&
            clauses.some(function (param) { return param.condition.startsWith('>'); }) &&
            clauses.some(function (param) { return param.condition.startsWith('<'); }));
    };
    Query.prototype.orderBy = function (field, direction$) {
        return new Query(this.params$, this.limitValue$, this.orderByValue$
            ? Rx.combineLatest([this.orderByValue$, toObservable(direction$)]).pipe(map(function (_a) {
                var orderBy = _a[0], direction = _a[1];
                return orderBy.concat([{ field: field, direction: direction }]);
            }))
            : toObservable(direction$).pipe(map(function (direction) { return [{ field: field, direction: direction }]; })));
    };
    Query.prototype.limit = function (n$) {
        if (this.limitValue$) {
            throw new Error("You can't call `limit` twice.");
        }
        return new Query(this.params$, toObservable(n$).pipe(distinctUntilChanged()), this.orderByValue$);
    };
    /**
     * An empty query which does not trigger a network request. To be used with
     * dynamic queries (queries wrapped in Observables).
     *
     * Note that this will always result in an empty list, and is not the same as
     * passing the default query (`query => query`) or no query (`undefined`).
     */
    Query.prototype.null = function () {
        return new NullQuery();
    };
    Object.defineProperty(Query.prototype, "query", {
        // Grab the properties of the query as observables
        get: function () {
            return {
                limit$: this.limitValue$ || Rx.of(undefined),
                params$: this.params$ || Rx.of({}),
                order$: this.orderByValue$ || Rx.of(undefined),
            };
        },
        enumerable: true,
        configurable: true
    });
    return Query;
}());
export { Query };
